import { useEffect, useRef, useState } from 'react';
import { api, ErreurApi } from '../api.js';
import { tokens } from '../tokens.js';
import {
  Badge, BoiteErreur, labelChamp, inputBase,
  boutonPrincipal, boutonSecondaire, formaterDateLongue,
} from '../helpers.jsx';
import {
  Mail, User, Shield, Send, Save, ToggleLeft, ToggleRight,
  ChevronDown, ChevronRight, Code,
} from 'lucide-react';

// Gestion des gabarits mail : toggle, edition sujet/corps, test.
export function AdminGabaritsMail({ utilisateurActuel }) {
  const [gabarits, setGabarits] = useState([]);
  const [chargement, setCh]     = useState(true);
  const [erreur, setErreur]     = useState('');
  const [ouverts, setOuverts]   = useState({});
  const [enEdition, setEdition] = useState({});
  const [enSauve, setEnSauve]   = useState(null);
  const [testEnvoi, setTestEnv] = useState(null);
  const [testRetour, setTestRet]= useState(null);
  const [testEmail, setTestEmail] = useState(utilisateurActuel.email || '');

  function recharger() {
    setCh(true);
    api.get('/admin/gabarits-mail')
      .then(d => setGabarits(d.gabarits || []))
      .catch(e => setErreur(e.message))
      .finally(() => setCh(false));
  }
  useEffect(recharger, []);

  function basculerOuvert(scenario) {
    setOuverts(o => ({ ...o, [scenario]: !o[scenario] }));
    if (!enEdition[scenario]) {
      const g = gabarits.find(x => x.scenario === scenario);
      if (g) setEdition(e => ({ ...e, [scenario]: { sujet: g.sujet, corps: g.corps, actif: g.actif } }));
    }
  }

  function modifier(scenario, champ, valeur) {
    setEdition(e => ({ ...e, [scenario]: { ...e[scenario], [champ]: valeur } }));
  }

  async function sauvegarder(scenario) {
    const ed = enEdition[scenario];
    if (!ed) return;
    setEnSauve(scenario); setErreur('');
    try {
      const rep = await api.put(`/admin/gabarits-mail/${scenario}`, {
        sujet: ed.sujet, corps: ed.corps, actif: ed.actif,
      });
      setGabarits(gs => gs.map(g => g.scenario === scenario ? rep.gabarit : g));
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    } finally { setEnSauve(null); }
  }

  async function basculerActif(scenario) {
    const g = gabarits.find(x => x.scenario === scenario);
    if (!g) return;
    try {
      const rep = await api.put(`/admin/gabarits-mail/${scenario}`, { actif: !g.actif });
      setGabarits(gs => gs.map(x => x.scenario === scenario ? rep.gabarit : x));
      setEdition(e => ({ ...e, [scenario]: { ...e[scenario], actif: rep.gabarit.actif } }));
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  async function envoyerTest(scenario) {
    if (!testEmail || !testEmail.includes('@')) {
      setTestRet({ scenario, ok: false, message: 'Adresse email destinataire invalide' });
      return;
    }
    setTestEnv(scenario); setTestRet(null);
    try {
      const rep = await api.post('/admin/gabarits-mail/test', {
        scenario,
        email: testEmail,
      });
      setTestRet({
        scenario,
        ok: !!rep.ok,
        message: rep.ok ? `SMTP a accepté l'envoi à ${testEmail}` : (rep.erreur || 'Echec'),
      });
    } catch (e) {
      setTestRet({ scenario, ok: false, message: e instanceof ErreurApi ? e.message : 'Erreur' });
    } finally { setTestEnv(null); }
  }

  const gabUser  = gabarits.filter(g => g.destinataire === 'user');
  const gabAdmin = gabarits.filter(g => g.destinataire === 'admin');

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: tokens.ink }}>
          Modèles de mail
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: tokens.inkSoft }}>
          Active/désactive chaque type d'événement, personnalise le sujet et le corps.
          Le clic sur une variable l'insère dans le corps à l'endroit du curseur.
        </p>
      </div>

      {erreur && <BoiteErreur message={erreur} />}
      {chargement && <div style={etat}>Chargement…</div>}

      {!chargement && (
        <>
          <Section titre="Aux utilisateurs" Icon={User} count={gabUser.length}>
            {gabUser.map(g => (
              <CarteGabarit
                key={g.id} gabarit={g}
                ouvert={!!ouverts[g.scenario]}
                edition={enEdition[g.scenario]}
                onToggle={() => basculerOuvert(g.scenario)}
                onActif={() => basculerActif(g.scenario)}
                onChange={(c, v) => modifier(g.scenario, c, v)}
                onSauver={() => sauvegarder(g.scenario)}
                enSauve={enSauve === g.scenario}
                onTest={(email) => { if (email) setTestEmail(email); envoyerTest(g.scenario); }}
                testEmail={testEmail}
                onTestEmail={setTestEmail}
                testEnvoi={testEnvoi === g.scenario}
                testRetour={testRetour && testRetour.scenario === g.scenario ? testRetour : null}
              />
            ))}
          </Section>

          <Section titre="Aux administrateurs" Icon={Shield} count={gabAdmin.length}>
            {gabAdmin.map(g => (
              <CarteGabarit
                key={g.id} gabarit={g}
                ouvert={!!ouverts[g.scenario]}
                edition={enEdition[g.scenario]}
                onToggle={() => basculerOuvert(g.scenario)}
                onActif={() => basculerActif(g.scenario)}
                onChange={(c, v) => modifier(g.scenario, c, v)}
                onSauver={() => sauvegarder(g.scenario)}
                enSauve={enSauve === g.scenario}
                onTest={(email) => { if (email) setTestEmail(email); envoyerTest(g.scenario); }}
                testEmail={testEmail}
                onTestEmail={setTestEmail}
                testEnvoi={testEnvoi === g.scenario}
                testRetour={testRetour && testRetour.scenario === g.scenario ? testRetour : null}
              />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ titre, Icon, count, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{
        margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: tokens.inkSoft,
        textTransform:'uppercase', letterSpacing: 0.5,
        display:'flex', alignItems:'center', gap: 8,
      }}>
        <Icon size={14} /> {titre} ({count})
      </h3>
      <div style={{ display:'flex', flexDirection:'column', gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

function CarteGabarit({
  gabarit, ouvert, edition,
  onToggle, onActif, onChange, onSauver, enSauve,
  onTest, testEnvoi, testRetour, testEmail, onTestEmail,
}) {
  const corpsRef = useRef(null);

  function insererVar(nom) {
    const ta = corpsRef.current;
    if (!ta) return;
    const start = ta.selectionStart || 0;
    const end = ta.selectionEnd || 0;
    const cible = '{' + nom + '}';
    const ancien = edition?.corps ?? gabarit.corps;
    const nouveau = ancien.slice(0, start) + cible + ancien.slice(end);
    onChange('corps', nouveau);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + cible.length, start + cible.length);
    });
  }

  const modifie = edition && (
    edition.sujet !== gabarit.sujet
    || edition.corps !== gabarit.corps
    || edition.actif !== gabarit.actif
  );

  return (
    <div style={{
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: 10,
      opacity: gabarit.actif ? 1 : 0.65,
    }}>
      <div
        onClick={onToggle}
        style={{
          display:'flex', alignItems:'center', gap: 10,
          padding: '12px 14px', cursor:'pointer',
        }}
      >
        {ouvert ? <ChevronDown size={14} color={tokens.inkSoft}/> : <ChevronRight size={14} color={tokens.inkSoft}/>}
        <Mail size={14} color={gabarit.actif ? tokens.accent : tokens.inkMuted} />
        <strong style={{ fontSize: 13, color: tokens.ink }}>{gabarit.libelle}</strong>
        {!gabarit.actif && <Badge bg={tokens.dangerBg} fg={tokens.danger} taille="petit">Désactivé</Badge>}
        <span style={{ fontSize: 11, color: tokens.inkMuted, fontFamily:'monospace', marginLeft: 4 }}>
          {gabarit.scenario}
        </span>

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap: 8 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onActif(); }}
            title={gabarit.actif ? 'Désactiver' : 'Activer'}
            style={{
              background:'transparent', border:'none', cursor:'pointer',
              color: gabarit.actif ? tokens.success : tokens.inkMuted,
              display:'flex', alignItems:'center',
            }}
          >
            {gabarit.actif ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
          </button>
        </div>
      </div>

      {ouvert && edition && (
        <div style={{ padding: '0 14px 14px', borderTop: `1px solid ${tokens.border}` }}>
          <div style={{ marginTop: 12 }}>
            <label style={labelChamp}>Sujet</label>
            <input
              value={edition.sujet}
              onChange={(e) => onChange('sujet', e.target.value)}
              style={inputBase}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={{ ...labelChamp, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span>Corps</span>
              <span style={{ fontWeight: 400, color: tokens.inkMuted, fontSize: 10 }}>
                {edition.corps.length} caractères
              </span>
            </label>
            <textarea
              ref={corpsRef}
              value={edition.corps}
              onChange={(e) => onChange('corps', e.target.value)}
              rows={Math.max(6, Math.min(20, edition.corps.split('\n').length + 1))}
              style={{
                ...inputBase, fontFamily: 'monospace', fontSize: 12,
                resize: 'vertical', lineHeight: 1.5,
              }}
            />
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={{
              fontSize: 10, color: tokens.inkMuted,
              textTransform:'uppercase', letterSpacing: 0.5,
              marginBottom: 6, display:'flex', alignItems:'center', gap: 4,
            }}>
              <Code size={11} /> Variables (clic pour insérer dans le corps)
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap: 4 }}>
              {gabarit.variables_dispo.map(v => (
                <button
                  key={v}
                  onClick={() => insererVar(v)}
                  style={{
                    background: tokens.surfaceAlt,
                    border: `1px solid ${tokens.border}`,
                    color: tokens.inkSoft,
                    fontFamily: 'monospace', fontSize: 11,
                    padding: '2px 6px', borderRadius: 4, cursor:'pointer',
                  }}
                >
                  {'{' + v + '}'}
                </button>
              ))}
            </div>
          </div>

          {testRetour && (
            <div style={{
              marginTop: 12, padding: '8px 12px',
              background: testRetour.ok ? tokens.successBg : tokens.dangerBg,
              color: testRetour.ok ? tokens.success : tokens.danger,
              borderRadius: 6, fontSize: 12,
            }}>
              {testRetour.message}
            </div>
          )}

          <div style={{ display:'flex', gap: 8, marginTop: 14, alignItems:'center', flexWrap:'wrap' }}>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => onTestEmail(e.target.value)}
              placeholder="Adresse de test"
              style={{
                ...inputBase, flex: 1, minWidth: 200,
                padding: '6px 10px', fontSize: 12,
              }}
            />
            <button
              onClick={() => onTest(testEmail)}
              disabled={testEnvoi}
              style={boutonSecondaire}
              title="Envoie un test à l'adresse saisie ci-dessus avec des données factices"
            >
              <Send size={13} /> {testEnvoi ? 'Envoi…' : "Tester"}
            </button>
            <button
              onClick={onSauver}
              disabled={enSauve || !modifie}
              style={{ ...boutonPrincipal, opacity: modifie ? 1 : 0.5 }}
            >
              <Save size={13} /> {enSauve ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>

          <div style={{ marginTop: 8, fontSize: 10, color: tokens.inkMuted, textAlign:'right' }}>
            Modifié le {formaterDateLongue(gabarit.mis_a_jour_le)}
          </div>
        </div>
      )}
    </div>
  );
}

const etat = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 32,
  textAlign:'center',
  color: tokens.inkSoft,
  fontSize: 14,
};
